import { useEditorStore, type Plugin } from '@modules/editor';
import { getGenericBounds } from '@modules/editor/src/lib/bound';
import { getElementAtPosition as getEditorElementAtPosition } from '@modules/editor/src/lib/collision';
import {
  addElement,
  defaultGenericElement,
  type GenericElement,
} from '@modules/editor/src/lib/element';
import { renderStaticScene } from '@modules/editor/src/lib/scene';

import { getObjectAtPosition } from '$/pages/InspirationEditorPage/lib/objectHitDetection';
import { useInspirationEditorStore } from '$/pages/InspirationEditorPage/stores/useInspirationEditorStore';

export interface InspirationImageElement extends GenericElement {
  image: HTMLCanvasElement;
  originalWidth: number;
  originalHeight: number;
  type: 'inspiration';
}

const inspirationImageActions = {
  addCenterImage: (
    source: HTMLImageElement | { width: number; height: number },
    options: { id?: string; scale?: number; isVisible?: boolean } = {},
  ) => {
    const naturalWidth =
      source instanceof HTMLImageElement ? source.naturalWidth : source.width;
    const naturalHeight =
      source instanceof HTMLImageElement ? source.naturalHeight : source.height;

    const canvas = document.createElement('canvas');
    canvas.width = naturalWidth;
    canvas.height = naturalHeight;
    const imageCtx = canvas.getContext('2d')!;
    if (source instanceof HTMLImageElement) {
      imageCtx.drawImage(source, 0, 0);
    }

    const imageWidth = window.innerWidth * (options.scale ?? 0.6);
    const imageHeight = (naturalHeight / naturalWidth) * imageWidth;

    const defaults = defaultGenericElement();
    addElement(useEditorStore.getState().elements, {
      ...defaults,
      id: options.id || defaults.id,
      height: imageHeight,
      image: canvas,
      isLocked: true,
      layer: -1,
      type: 'inspiration',
      width: imageWidth,
      originalWidth: naturalWidth,
      originalHeight: naturalHeight,
      x: window.innerWidth / 2 - imageWidth / 2,
      y: window.innerHeight / 2 - imageHeight / 2,
      isVisible: options.isVisible ?? defaults.isVisible,
    } as InspirationImageElement);

    if (options.isVisible ?? true) {
      renderStaticScene();
    }
  },
};

const MOVE_THRESHOLD = 10;

const inspirationState = {
  lastPosition: null as { x: number; y: number } | null,
};

const hasMovedEnough = (x: number, y: number) => {
  if (!inspirationState.lastPosition) return true;
  const dx = x - inspirationState.lastPosition.x;
  const dy = y - inspirationState.lastPosition.y;
  return Math.sqrt(dx * dx + dy * dy) >= MOVE_THRESHOLD;
};

const plugin: Plugin<InspirationImageElement, typeof inspirationImageActions> =
  {
    name: 'inspiration',
    cursor: 'pointer',
    actions: inspirationImageActions,
    events: {
      onPointerDown: async ({ event, helpers, runtimeState, elementState }) => {
        const { button, pos } = event;
        const coords = helpers.sceneCoordsToViewportCoords(pos);

        if (button === 'left') {
          const activeTransformation = runtimeState.activeTransformation;
          if (activeTransformation) {
            const transformedElementId = Array.from(
              activeTransformation.originalElements.keys(),
            ).find((id) => id.startsWith('corners/'));
            if (transformedElementId) {
              return;
            }
          }

          const selectedElementIds = elementState.selectedElementIds;
          const hasSelectedCorner = selectedElementIds.some((id) =>
            id.startsWith('corners/'),
          );
          if (hasSelectedCorner) {
            return;
          }

          const clickedElement = getEditorElementAtPosition(pos.x, pos.y);
          if (clickedElement?.id.startsWith('corners/')) {
            return;
          }

          const masks = useInspirationEditorStore
            .getState()
            .getAvailableMasks();
          const hit = await getObjectAtPosition(
            Object.keys(masks),
            coords.x,
            coords.y,
          );

          if (!hit) return;

          if (
            masks[hit].id ===
            useInspirationEditorStore.getState().activeComponentId
          ) {
            return useInspirationEditorStore
              .getState()
              .toggleActiveComponent(undefined);
          }

          useInspirationEditorStore
            .getState()
            .toggleActiveComponent(masks[hit].id);
        }

        if (button === 'middle') {
          helpers.setCursor('grabbing');
        }
      },
      onPointerMove: async ({ event, helpers }) => {
        const { button, pos } = event;
        const coords = helpers.sceneCoordsToViewportCoords(pos);

        if (button === 'left' || button === 'middle') {
          return helpers.setCursor('grabbing');
        }

        if (!hasMovedEnough(coords.x, coords.y)) return;
        inspirationState.lastPosition = coords;

        const masks = useInspirationEditorStore.getState().getAvailableMasks();
        const hit = await getObjectAtPosition(
          Object.keys(masks),
          coords.x,
          coords.y,
        );

        if (!hit) {
          helpers.setCursor('grab');
          return useInspirationEditorStore.setState({
            hoveredComponentId: undefined,
          });
        }

        helpers.setCursor('pointer');
        useInspirationEditorStore.setState({
          hoveredComponentId: masks[hit].id,
        });
      },
      onPointerUp: async ({ event, helpers }) => {
        const { pos } = event;
        const coords = helpers.sceneCoordsToViewportCoords(pos);

        const masks = useInspirationEditorStore.getState().getAvailableMasks();
        const hit = await getObjectAtPosition(
          Object.keys(masks),
          coords.x,
          coords.y,
        );

        if (!hit) {
          helpers.setCursor('grab');
        }
      },
    },
    render: {
      draw: ({ config, context, element }) => {
        const radius = 4;

        const { height, width } = element;

        context.beginPath();
        context.moveTo(radius, 0);
        context.arcTo(width, 0, width, height, radius); // top-right corner
        context.arcTo(width, height, 0, height, radius); // bottom-right corner
        context.arcTo(0, height, 0, 0, radius); // bottom-left corner
        context.arcTo(0, 0, width, 0, radius); // top-left corner
        context.clip();

        if (element.opacity) {
          context.globalAlpha = element.opacity;
        }

        if (element.isDeletedFlag) {
          context.globalAlpha = config.canvas.deletingElementOpacity;
        }

        context.drawImage(element.image, 0, 0, element.width, element.height);
      },
    },
    collision: {
      bounds: (element) => getGenericBounds(element),
      calculateShape: () => ({ type: 'area', data: '' }),
      isPointInShape: () => false,
      isPointOnShape: () => false,
    },
    transformation: {
      shiftBySizeOnFlip: false,
      resizedBounds: () => [0, 0, 0, 0, 0, 0],
    },
  };

export const inspirationPlugin = plugin;
